import { ButtonType, ButtonWidget, Img, InteractionState } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { FavoriteToggle } from '~src/favorites/components/favoriteToggle/FavoriteToggle';
import { FavoriteToggleWithCounts } from '~src/favorites/components/favoriteToggle/FavoriteToggleWithCounts';
import { FavoriteToggleWithLocalState } from '~src/favorites/components/favoriteToggle/FavoriteToggleWithLocalState';

import { ActionsMenu } from '../../components/ActionsMenu/ActionsMenu';
import { SpaceInfoItemType } from '../../models/ContentType';
import { ISpaceItemDetailInfo } from '../../models/ISpaceItemDetailInfo';
import { DetailButton } from './DetailButton';
import { SpaceHeaderStyles } from './SpaceHeaderStyles';

// The subset of SpaceInfoItemType whose ISpaceItemDetailInfo<C> has all the attributes needed
// to play nice with the below widgets
type HeaderIconSpaceInfoItemType = 'workbook' | 'datasource' | 'flow' | 'project' | 'dataRole' | 'lens' | 'virtualConnection' | 'virtualConnectionTable' | 'collection' | 'predictiveModel';
type GroupHeaderIconSpaceInfoItemType = 'group';
export function FavoriteToggleIcon<C extends HeaderIconSpaceInfoItemType>(params: {
  readonly item: ISpaceItemDetailInfo<C>;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
}): JSX.Element {
  const { item, addToFavorites, removeFromFavorites } = params;

  return <FavoriteToggle
    isTabbable={true}
    isFavorite={item.favorite}
    onAddFavorite={addToFavorites}
    onRemoveFavorite={removeFromFavorites}
  />;
}

export function FavoriteToggleWithCountsIcon<C extends HeaderIconSpaceInfoItemType>(params: {
  readonly item: ISpaceItemDetailInfo<C>;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
}): JSX.Element {
  const { item, addToFavorites, removeFromFavorites } = params;
  const usageInfo = item.usageInfo;

  // If its a flow, it does not have usageInfo implemented yet
  if (item.contentType === 'flow') {
    return <FavoriteToggleWithLocalState
      isTabbable={true}
      isFavorite={item.favorite}
      onAddFavorite={addToFavorites}
      onRemoveFavorite={removeFromFavorites}
    />;
  }
  // in case an older server is connecting to newer front-end
  if (_.isUndefined(usageInfo) || !usageInfo.hasOwnProperty('favoritesTotal')) {
    return FavoriteToggleIcon(params);
  }

  return <FavoriteToggleWithCounts
    canHaveKeyboardFocus={true}
    isFavorite={item.favorite}
    onAddFavorite={addToFavorites}
    onRemoveFavorite={removeFromFavorites}
    favoritesCount={usageInfo.favoritesTotal}
  />;
}

export function DetailButtonIcon<C extends SpaceInfoItemType>(
  item: ISpaceItemDetailInfo<C>,
  openDetail: () => void,
): JSX.Element {
  return <DetailButton testId={`${item.contentType}-${item.id}`} onClick={openDetail}/>;
}

export function ActionsMenuIcon<C extends HeaderIconSpaceInfoItemType>(
  item: ISpaceItemDetailInfo<C>,
  disabled?: boolean,
  buttonTitle?: string,
): JSX.Element {
  return <ActionsMenu buttonTitle={buttonTitle} targetItems={[ item ]} useIcon={true} disabled={disabled}/>;
}

export function GroupActionsMenuIcon<C extends GroupHeaderIconSpaceInfoItemType>(
  item: ISpaceItemDetailInfo<C>,
  disabled?: boolean,
  buttonTitle?: string,
): JSX.Element {
  return <ActionsMenu buttonTitle={buttonTitle} targetItems={[ item ]} useIcon={true} disabled={disabled}/>;
}

export interface ShareButtonProps {
  readonly onClick: () => void;
  readonly testId: string;
}

export function ShareButton(props: ShareButtonProps) {
  return (
    <ButtonWidget
      buttonType={ButtonType.Icon}
      getContent={ToolbarShareIcon}
      onClick={props.onClick}
      style={SpaceHeaderStyles.IconButtonStyleOverrides}
      testId={props.testId}
      title={Messages.shareButtonCollection()}
    />
  );
}

function ToolbarShareIcon(interactiveState: InteractionState) {
return (
    <Img
      draggable={false}
      src={imgPath('share_18_large.svg')}
    />
  );
}
